import { ChevronDown, ChevronRight, Terminal, Wrench } from 'lucide-react';
import { type ComponentProps, Fragment } from 'react';
import { z } from 'zod';

import { PhoebeChatDebugEventTimeline } from '@apps/web/components/phoebe_chat_debug_event_timeline';
import { PhoebeChatDebugRunStats } from '@apps/web/components/phoebe_chat_debug_sidebar';
import { type PhoebeChatDebugTimelineEntry } from '@apps/web/components/phoebe_chat_debug_snapshot';
import { Markdown } from '@apps/web/components/ui/markdown';
import { cn } from '@apps/web/lib/utils';

const displayValueSchema = z.union([
    z.string(),
    z.json().transform((value) => JSON.stringify(value, null, 2)),
]);

function BashCommand({ command }: { readonly command: string }) {
    const tokens = command.split(
        /("(?:\\.|[^"\\])*"|'[^']*'|\$\{[^}]*\}|\$[\w]+|#[^\n]*|&&|\|\||[|;<>])/gu,
    );
    return (
        <pre className="bg-admin-sunken overflow-x-auto rounded-md p-3 text-xs leading-6">
            <code className="language-bash">
                {tokens.map((token, index) => (
                    <span
                        key={index}
                        className={cn(
                            token.startsWith('#') && 'text-admin-ink-muted',
                            (token.startsWith('"') || token.startsWith("'")) &&
                                'text-admin-success',
                            (token.startsWith('$') ||
                                /^[|;&<>]+$/u.test(token)) &&
                                'text-admin-purple',
                        )}
                    >
                        {token}
                    </span>
                ))}
            </code>
        </pre>
    );
}

export function AgentTraceTranscriptEntry({
    entry,
    isExpanded,
    onToggle,
}: {
    readonly entry: PhoebeChatDebugTimelineEntry;
    readonly isExpanded: boolean;
    readonly onToggle: () => void;
}) {
    const {
        argumentsJson: args,
        command,
        toolName,
        description,
        prose,
        output,
        error,
        label,
        detail,
    } = entry.content;
    const reasoning = entry.type === 'assistant_reasoning';
    if (
        reasoning ||
        entry.type === 'assistant_text' ||
        entry.type === 'user_message'
    ) {
        return (
            <article
                className={cn(
                    'px-2 py-4',
                    reasoning && 'text-admin-ink-muted italic',
                )}
            >
                <p className="text-admin-ink-muted mb-2 text-xs font-medium not-italic">
                    {reasoning
                        ? 'Reasoning'
                        : entry.type === 'user_message'
                          ? 'User'
                          : 'Assistant'}
                </p>
                <Markdown>{prose ?? ''}</Markdown>
            </article>
        );
    }
    const isTool =
        !!toolName ||
        entry.type === 'tool_call' ||
        entry.type === 'tool_output';
    const bash = toolName === 'run_bash' && entry.type === 'tool_call';
    const ExpandIcon = isExpanded ? ChevronDown : ChevronRight;
    const ToolIcon = bash ? Terminal : Wrench;
    return (
        <div className="border-admin-border border-b px-2 py-1">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                aria-label={[
                    toolName ?? label ?? entry.type.replace(/_/gu, ' '),
                    entry.type === 'tool_output' ? 'output' : null,
                    description ?? detail,
                ]
                    .filter(Boolean)
                    .join(' ')}
                className="text-admin-ink-secondary hover:bg-admin-surface-hover flex min-h-10 w-full items-center gap-2 rounded-md text-left text-xs"
            >
                <ExpandIcon className="size-3.5 shrink-0" aria-hidden={true} />
                {isTool ? (
                    <ToolIcon
                        className="size-3.5 shrink-0"
                        aria-hidden={true}
                    />
                ) : null}
                <span className="shrink-0 font-medium">
                    {toolName ?? label ?? entry.type.replace(/_/gu, ' ')}
                </span>
                {entry.type === 'tool_output' ? (
                    <span className="text-admin-ink-muted">output</span>
                ) : null}
                <span className="text-admin-ink-muted truncate">
                    {description ?? detail}
                </span>
            </button>
            {bash && command ? <BashCommand command={command} /> : null}
            {isExpanded ? (
                <div className="space-y-3 py-3 pl-6 text-xs">
                    {isTool && Object.keys(args).length > 0 ? (
                        <dl className="space-y-2">
                            {Object.entries(args)
                                .filter(
                                    ([name]) =>
                                        !bash ||
                                        (name !== 'command' &&
                                            name !== 'description'),
                                )
                                .map(([name, value]) => (
                                    <Fragment key={name}>
                                        <dt className="text-admin-ink-muted font-medium">
                                            {name.replace(/_/gu, ' ')}
                                        </dt>
                                        <dd>
                                            <pre className="overflow-x-auto break-words whitespace-pre-wrap">
                                                {displayValueSchema.parse(
                                                    value,
                                                )}
                                            </pre>
                                        </dd>
                                    </Fragment>
                                ))}
                        </dl>
                    ) : null}
                    {error ? (
                        <p role="alert" className="text-destructive">
                            {error}
                        </p>
                    ) : null}
                    {output !== undefined ? (
                        <section aria-label="Tool output">
                            <p className="text-admin-ink-muted mb-1 font-medium">
                                Output
                            </p>
                            <pre className="bg-admin-sunken max-h-96 overflow-auto rounded-md p-3 break-words whitespace-pre-wrap">
                                {displayValueSchema.parse(output)}
                            </pre>
                        </section>
                    ) : null}
                    <details>
                        <summary className="text-admin-ink-muted cursor-pointer">
                            Raw event
                        </summary>
                        <pre className="mt-2 max-h-96 overflow-auto break-words whitespace-pre-wrap">
                            {JSON.stringify(entry.raw, null, 2)}
                        </pre>
                    </details>
                </div>
            ) : null}
        </div>
    );
}

export function AgentTraceTranscript({
    snapshot,
    ...props
}: Omit<
    ComponentProps<typeof PhoebeChatDebugEventTimeline>,
    'entryComponent'
>) {
    return (
        <div className="flex h-full min-h-0 flex-col">
            <details className="border-admin-border shrink-0 border-b px-4 py-2 text-xs">
                <summary className="text-admin-ink-muted cursor-pointer">
                    Run details
                </summary>
                <div className="max-h-64 overflow-auto">
                    <PhoebeChatDebugRunStats snapshot={snapshot} />
                </div>
            </details>
            <PhoebeChatDebugEventTimeline
                {...props}
                snapshot={snapshot}
                title="Transcript"
                entryComponent={AgentTraceTranscriptEntry}
            />
        </div>
    );
}
